/**
 * L'affiche à imprimer — eventmatch.pro/affiche/<slug>.
 *
 * Reprend la maquette fournie par Tom : le titre, les quatre promesses, le QR
 * en évidence, et la signature. Deux règles de fabrication :
 *   · tout est en CSS, rien en image — l'impression reste nette à n'importe
 *     quelle taille, et le texte se traduit ;
 *   · le QR porte le code de CELUI QUI IMPRIME : qui le scanne s'inscrit par
 *     son lien, devient son filleul, et arrive sur les murs.
 *
 * `@media print` retire tout ce qui n'est pas l'affiche : on imprime la page
 * telle quelle, sans capture d'écran ni PDF à fabriquer.
 */
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Printer, Share2, Target, Users } from 'lucide-react';
import { appelerApi } from '../reseau.ts';
import { Marque } from '../composants/Marque.tsx';
import type { Affiche as DonneesAffiche } from './compte/types.ts';

export function Affiche({ slug }: { slug: string }) {
  const [a, setA] = useState<DonneesAffiche | null | undefined>(undefined);

  useEffect(() => {
    void appelerApi<DonneesAffiche>(`/api/parrainage/affiche/${encodeURIComponent(slug)}`).then((r) => setA(r.donnees ?? null));
  }, [slug]);

  if (a === undefined) return <p className="min-h-screen bg-void p-10 text-fumee">Chargement…</p>;
  if (a === null) return <Navigate to="/" replace />;

  const date = new Date(a.evenement.debut_le).toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Brussels' });

  return (
    <div className="min-h-screen bg-void font-sans text-blanc">
      {/* Barre d'outils — invisible à l'impression */}
      <div className="mx-auto flex max-w-[860px] flex-wrap items-center justify-between gap-3 px-6 py-5 print:hidden">
        <div>
          <p className="text-[15px]">Votre affiche pour <span className="font-medium">{a.evenement.nom}</span></p>
          <p className="mt-1 text-[13px] text-fumee">
            Imprimez-la, posez-la sur votre table ou votre stand. Qui la scanne s’inscrit par votre lien : il devient
            votre filleul, et vous touchez 5 % de ses dépenses.
          </p>
        </div>
        <button onClick={() => window.print()} className="btn-creme !min-h-0 px-5 py-2 text-[14px]">
          <Printer size={14} strokeWidth={1.5} className="mr-1.5 inline" />
          Imprimer
        </button>
      </div>

      {/* L'affiche — A4 portrait */}
      <div className="mx-auto max-w-[860px] px-6 pb-16 print:max-w-none print:px-0 print:pb-0">
        <div className="affiche relative overflow-hidden rounded-[18px] bg-void p-10 print:rounded-none print:p-[12mm]">
          <div className="pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full opacity-25 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(255,90,120,0.5), transparent 70%)' }} />

          <header className="relative flex items-center gap-4">
            <Marque taille={54} />
            <p className="text-[42px] font-semibold leading-none tracking-tight">
              EventMatch<span className="text-fumee">.pro</span>
            </p>
          </header>
          <p className="label-caps relative mt-3 text-fumee">Connectez · Échangez · Développez</p>

          <h1 className="relative mt-9 text-[46px] font-bold leading-[1.05]">
            Le réseau B2B<br />qui fait <span className="text-match-gradient">la différence</span>
          </h1>
          <p className="relative mt-4 max-w-[420px] text-[17px] leading-relaxed text-blanc/80">
            Des opportunités, des rencontres, des partenariats qui créent de la valeur.
          </p>

          <div className="relative mt-9 flex flex-wrap items-start justify-between gap-10">
            <ul className="flex max-w-[400px] flex-col gap-5">
              <Promesse icone={<Share2 size={18} strokeWidth={1.75} />} titre="ÊTRE VISIBLE" texte="sur le Deal Wall" />
              <Promesse icone={<Users size={18} strokeWidth={1.75} />} titre="SUIVRE LE PROGRAMME," texte="retrouver les intervenants et sponsors" />
              <Promesse icone={<Target size={18} strokeWidth={1.75} />} titre="FAIRE UN PERFECT MATCH" texte="avec le partenaire idéal" />
              <Promesse icone={<Share2 size={18} strokeWidth={1.75} />} titre="GAGNER EN INFLUENCE" texte="en rejoignant d’autres évènements" />
            </ul>

            <div className="text-center">
              <p className="text-[15px] font-bold uppercase tracking-[0.06em]">Scannez ce<br />QR code</p>
              <div className="relative mt-4 inline-block p-3">
                <span className="absolute left-0 top-0 h-7 w-7 border-l-[3px] border-t-[3px] border-blanc" />
                <span className="absolute right-0 top-0 h-7 w-7 border-r-[3px] border-t-[3px] border-blanc" />
                <span className="absolute bottom-0 left-0 h-7 w-7 border-b-[3px] border-l-[3px] border-blanc" />
                <span className="absolute bottom-0 right-0 h-7 w-7 border-b-[3px] border-r-[3px] border-blanc" />
                {a.qr ? (
                  <img src={a.qr} alt={`QR vers ${a.url}`} className="h-[220px] w-[220px] rounded-[6px] bg-blanc p-2" />
                ) : (
                  <span className="flex h-[220px] w-[220px] items-center justify-center rounded-[6px] bg-blanc text-void">QR indisponible</span>
                )}
              </div>
              <p className="mt-3 text-[13px] text-fumee">et rejoignez</p>
              <p className="mt-1 flex items-center justify-center gap-2 text-[18px] font-semibold">
                <Marque taille={22} /> EventMatch<span className="text-fumee">.pro</span>
              </p>
              <p className="mt-2 text-[12px] text-fumee">Code : <span className="text-blanc">{a.code}</span></p>
            </div>
          </div>

          <footer className="relative mt-10 border-t border-graphite pt-6">
            <p className="label-caps text-fumee">Plus qu’un évènement,</p>
            <p className="mt-1 text-[30px] font-bold leading-tight">
              <span className="text-match-gradient">UN ÉCOSYSTÈME</span><br />DE BUSINESS
            </p>
            <p className="mt-4 text-[13px] text-fumee">
              {a.evenement.nom} · {date}
              {a.evenement.lieu_ville ? ` · ${a.evenement.lieu_ville}` : ''}
              {a.moi?.entreprise ? ` — affiche de ${a.moi.entreprise}` : ''}
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}

function Promesse({ icone, titre, texte }: { icone: React.ReactNode; titre: string; texte: string }) {
  return (
    <li className="flex items-start gap-4">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] border border-graphite text-match-rose">{icone}</span>
      <span>
        <span className="block text-[15px] font-bold uppercase tracking-[0.03em]">{titre}</span>
        <span className="block text-[15px] leading-snug text-blanc/75">{texte}</span>
      </span>
    </li>
  );
}
